<script setup lang="ts">
  import { computed } from 'vue';
  import { ChipSize, ChipAppearance } from './Chip.types';
  import { Typo, TypoVariant } from '@/components';

  interface ChipProps {
    appearance?: `${ChipAppearance}`;
    rounded?: boolean;
    leftRounded?: boolean;
    rightRounded?: boolean;
    active?: boolean;
    disabled?: boolean;
    size?: `${ChipSize}`;
  }

  const props = withDefaults(defineProps<ChipProps>(), {
    appearance: ChipAppearance.Outlined,
    rounded: false,
    active: false,
    disabled: false,
    leftRounded: false,
    rightRounded: false,
    size: ChipSize.S,
  });

  const subtextIsAllowed = computed(() => [ChipSize.M, ChipSize.L].includes(props.size));

  const TEXT_VARIANTS = {
    [ChipSize.L]: TypoVariant.BodyLRegularShort,
    [ChipSize.M]: TypoVariant.BodyMRegularShort,
    [ChipSize.S]: TypoVariant.BodyXSRegularShort,
  };

  const SUBTEXT_VARIANTS = {
    [ChipSize.L]: TypoVariant.BodyXSRegularShort,
    [ChipSize.M]: TypoVariant.CaptionMRegular,
  };

  const textVariant = computed(() => {
    return TEXT_VARIANTS[props.size];
  });

  const subtextVariant = computed(() => {
    return SUBTEXT_VARIANTS[props.size] || TypoVariant.BodyMRegular;
  });
</script>

<template>
  <div
    :class="[
      'tl-chip',
      `tl-chip_size-${size}`,
      `tl-chip_${appearance}`,
      {
        'tl-chip_active': active,
        'tl-chip_disabled': disabled,
        'tl-chip_rounded': rounded,
        'tl-chip_left-rounded': leftRounded,
        'tl-chip_right-rounded': rightRounded,
        'tl-chip_multiline': subtextIsAllowed && $slots.subtext,
      },
    ]"
    data-test-id="chip"
    v-on="$listeners"
  >
    <div class="tl-chip__content">
      <slot name="prepend" />
      <div class="tl-chip__source">
        <Typo class="tl-button__main-text" :variant="textVariant">
          <slot />
        </Typo>

        <Typo v-if="subtextIsAllowed && $slots.subtext" :variant="subtextVariant">
          <slot name="subtext" />
        </Typo>
      </div>
    </div>

    <slot name="append" />
  </div>
</template>

<style lang="scss" scoped>
  .tl-chip {
    $root: &;

    align-items: center;
    border: 1px solid;
    border-radius: var(--rounding-s);
    box-sizing: border-box;
    color: var(--color-text-primary);
    cursor: pointer;
    display: inline-flex;
    fill: var(--color-text-primary);
    gap: var(--spacing-xs);
    justify-content: center;
    -webkit-tap-highlight-color: transparent;
    width: max-content;

    &_filled {
      background: var(--color-slate-10);
      border-color: var(--color-slate-10);
    }

    &_outlined {
      background: var(--color-neutral-0);
      border-color: var(--color-neutral-30);
    }

    &_rounded {
      border-radius: var(--rounding-xl);
    }

    &_right-rounded {
      border-bottom-right-radius: var(--rounding-xl);
      border-top-right-radius: var(--rounding-xl);
    }

    &_left-rounded {
      border-bottom-left-radius: var(--rounding-xl);
      border-top-left-radius: var(--rounding-xl);
    }

    &_active {
      background: var(--color-slate-70);
      border-color: var(--color-slate-70);
      color: var(--color-text-inverse);
      fill: var(--color-text-inverse);
    }

    &_disabled {
      background: var(--color-neutral-10);
      border-color: var(--color-neutral-10);
      color: var(--color-text-tertiary);
      cursor: default;
      fill: var(--color-text-tertiary);

      &#{$root}_outlined {
        border-color: var(--color-slate-30);
      }
    }

    &_size {
      &-s {
        height: 24px;
        padding: 0 var(--spacing-xs);
      }

      &-m {
        height: 32px;
        padding: 0 var(--spacing-s);

        &#{$root}_multiline {
          height: 40px;

          &#{$root}_rounded {
            border-radius: 24px;
          }
        }
      }

      &-l {
        height: 40px;
        padding: 0 var(--spacing-m);

        &#{$root}_multiline {
          height: 50px;

          &#{$root}_rounded {
            border-radius: 32px;
          }
        }

        &#{$root}_rounded {
          border-radius: var(--rounding-xxl);
        }
      }
    }

    &__content {
      align-items: center;
      display: inline-flex;
      gap: var(--spacing-xxs);
    }

    &__source {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xxs);
    }
  }
</style>
